import { Controller } from 'react-hook-form';

import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  NumberInput,
  NumberInputField,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';

import { isDefined } from '@enterprise/core';
import { DefaultToolTip, NumberFormat } from '@enterprise/ui';

import type { PackageTargetEditorProps } from './types/package-target-editor.props';

import { usePackageTargetEditorConfiguration } from './hooks/package-target-editor-configuration.hook';

const PackageTargetEditor = ({ mailingId, packageTarget, totalFirstOutput }: PackageTargetEditorProps) => {
  const { control, errors, handleCancel, handleOpen, handleSubmit, isOpen, isSaveDisabled, isSaving, onSubmit } =
    usePackageTargetEditorConfiguration({ mailingId, packageTarget });

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      onClose={handleCancel}
      placement="top-start">
      <PopoverTrigger>
        <Badge
          as="button"
          colorScheme="purple"
          cursor="pointer"
          fontSize="xs"
          onClick={handleOpen}
          px={2}
          py={1}
          rounded="md"
          type="button">
          <DefaultToolTip
            isDisabled={isOpen}
            label="Click here to edit the package target">
            <NumberFormat value={packageTarget} />
          </DefaultToolTip>
        </Badge>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto">
          <PopoverArrow />
          <PopoverBody
            minW={300}
            p={3}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex
                direction="column"
                gap={2}>
                <FormControl isInvalid={isDefined(errors.targetQuantity)}>
                  <FormLabel>Edit Package Target</FormLabel>
                  <Controller
                    control={control}
                    name="targetQuantity"
                    render={({ field: { onChange, ref, value } }) => {
                      return (
                        <NumberInput
                          min={0}
                          onChange={(_, valueAsNumber) => {
                            onChange(Number.isNaN(valueAsNumber) ? undefined : valueAsNumber);
                          }}
                          value={value}>
                          <NumberInputField
                            placeholder="New target"
                            ref={ref}
                          />
                        </NumberInput>
                      );
                    }}
                    rules={{
                      max: {
                        message: [
                          'The package cannot have more names than the lists currently have available.',
                          'Setting it higher would have no effect.',
                        ].join('\n'),
                        value: totalFirstOutput,
                      },
                      min: { message: 'Value must be 0 or greater.', value: 0 },
                      required: 'Value must be 0 or greater.',
                      validate: (value) => {
                        return Number.isFinite(value) || 'Value must be a number.';
                      },
                    }}
                  />
                  <FormHelperText fontSize="xs">
                    Total names available <NumberFormat value={totalFirstOutput} />
                  </FormHelperText>
                  <FormErrorMessage whiteSpace="pre-line">{errors.targetQuantity?.message}</FormErrorMessage>
                </FormControl>
                <Flex
                  gap={2}
                  justify="flex-end"
                  mt={1}>
                  <Button
                    isDisabled={isSaving}
                    onClick={handleCancel}
                    variant="ghost">
                    Cancel
                  </Button>
                  <Button
                    colorScheme="green"
                    isDisabled={isSaveDisabled}
                    isLoading={isSaving}
                    type="submit">
                    Save
                  </Button>
                </Flex>
              </Flex>
            </form>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default PackageTargetEditor;
